import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from '@/routes/paths';
import { 
  Menu, Search, Bell, ChevronDown, User, 
  Sun, Moon, CreditCard, Settings, LogOut 
} from 'react-feather';
import { Dropdown } from 'react-bootstrap';
import { useAppTheme, useIsMobile } from '@/context/AppThemeProvider';
import { useAuth } from '@/context/AuthProvider';
import { useUserProfile } from '@/hooks/useUserProfile';

export const AppTopbar: React.FC = () => {
  const { theme, toggleTheme, toggleSidebar } = useAppTheme();
  const { user, signOut } = useAuth();
  const { profile } = useUserProfile();
  const isMobile = useIsMobile();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const displayName = profile?.display_name || 
    (user?.user_metadata?.firstName && user?.user_metadata?.lastName 
      ? `${user.user_metadata.firstName} ${user.user_metadata.lastName}`
      : user?.user_metadata?.name || user?.email || "User");
  
  // Use avatar_color from profile if available, otherwise use default gradient
  const avatarColor = profile?.avatar_color;
  const initials = displayName.substring(0, 2).toUpperCase();

  return (
    <header className="app-topbar navbar navbar-expand navbar-light bg-white border-bottom shadow-sm">
      <div className="topbar-left d-flex align-items-center gap-3">
        {/* Menu Toggle - Hamburger */}
        <button className="btn btn-link text-secondary p-2" onClick={toggleSidebar}>
          <Menu size={24} />
        </button>
        
        {/* Logo - In topbar, not sidebar */}
        <div className="logo d-flex align-items-center gap-2">
          <div className="logo-icon"></div>
          {!isMobile && <span className="logo-text fw-bold fs-4 text-primary">StonkFU</span>}
        </div>
        
        {/* Search Bar (desktop only) */}
        {!isMobile && (
          <div className="search-bar position-relative">
            <input 
              type="text" 
              className="form-control rounded-pill ps-3 pe-5"
              placeholder="Search stocks, portfolios, or settings..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search size={18} className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
          </div>
        )}
      </div>

      <div className="topbar-right d-flex align-items-center gap-3 ms-auto">
        {/* Notifications */}
        <Dropdown 
          show={notificationDropdownOpen}
          onToggle={setNotificationDropdownOpen}
        >
          <Dropdown.Toggle as="button" className="btn btn-link text-secondary position-relative notification-bell-btn">
            <Bell size={20} />
            <span className="position-absolute badge rounded-pill bg-danger" style={{ top: '0px', right: '0px', fontSize: '11px', padding: '3px 6px', minWidth: '20px' }}>3</span>
          </Dropdown.Toggle>
          
          <Dropdown.Menu align="end" className="notification-dropdown">
            <div className="dropdown-header">
              <h6>Notifications</h6>
              <Link to={PATHS.settings.notifications} className="view-all">View All</Link>
            </div>
            <div className="notification-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <div className="notification-item p-3 border-bottom">
                <div className="notification-content">
                  <h6 className="mb-1" style={{ fontSize: '13px' }}>Dividend Payment</h6>
                  <p className="mb-1 text-muted" style={{ fontSize: '12px' }}>JNJ dividend of $285 received</p>
                  <small className="text-muted" style={{ fontSize: '10px' }}>2 hours ago</small>
                </div>
              </div>
              <div className="notification-item p-3 border-bottom">
                <div className="notification-content">
                  <h6 className="mb-1" style={{ fontSize: '13px' }}>Portfolio Alert</h6>
                  <p className="mb-1 text-muted" style={{ fontSize: '12px' }}>NVDA reached your target price</p>
                  <small className="text-muted" style={{ fontSize: '10px' }}>5 hours ago</small>
                </div>
              </div>
              <div className="notification-item p-3">
                <div className="notification-content">
                  <h6 className="mb-1" style={{ fontSize: '13px' }}>AI Insight</h6>
                  <p className="mb-1 text-muted" style={{ fontSize: '12px' }}>New opportunity in tech sector</p>
                  <small className="text-muted" style={{ fontSize: '10px' }}>1 day ago</small>
                </div>
              </div>
            </div>
          </Dropdown.Menu>
        </Dropdown>
        
        {/* User Menu (desktop only) */}
        {!isMobile && (
          <Dropdown 
            show={userDropdownOpen}
            onToggle={setUserDropdownOpen}
          >
            <Dropdown.Toggle as="div" className="user-menu d-flex align-items-center gap-2 px-3 py-2 rounded-pill dropdown-toggle-no-caret" style={{ cursor: 'pointer' }}>
              <div 
                className="user-avatar"
                style={{ 
                  background: avatarColor 
                    ? avatarColor 
                    : `linear-gradient(135deg, #029AAB 0%, #00A8A8 50%, #00B89B 100%)`
                }}
              >
                {initials}
              </div>
              <span className="user-name fw-medium">{displayName as string}</span>
              <ChevronDown size={16} />
            </Dropdown.Toggle>
            
            <Dropdown.Menu align="end" className="user-dropdown">
              <Link to={PATHS.settings.profile} className="dropdown-item">
                <User size={16} />
                Profile
              </Link>
              
              <div 
                className="dropdown-item theme-toggle-item"
                onClick={(e) => e.stopPropagation()}
              >
                {theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
                <span>Theme</span>
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={theme === 'dark'}
                    onChange={toggleTheme}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <Link to={PATHS.settings.notifications} className="dropdown-item">
                <Bell size={16} />
                Notifications
              </Link>
              
              <Link to={PATHS.settings.subscription} className="dropdown-item">
                <CreditCard size={16} />
                Subscription
              </Link>
              
              <Link to={PATHS.settings.profile} className="dropdown-item">
                <Settings size={16} />
                Settings
              </Link>
              
              <div className="dropdown-divider" />
              
              <button onClick={signOut} className="dropdown-item">
                <LogOut size={16} />
                Sign Out
              </button>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    </header>
  );
};